"use client"

import { motion } from "framer-motion"
import {
  BarChart,
  EyeIcon,
  File,
  Handshake,
  LifeBuoy,
  LogOut,
  NotebookText,
  Search,
  Settings,
  SparklesIcon,
  Trash2,
  Warehouse,
  X,
} from "lucide-react"
import { useState } from "react"
import Profile from "../assets/demo.jpg"

const MobileMenu = ({ onClose, onSelectStory }) => {
  const [searchQuery, setSearchQuery] = useState("")

  const stories = [
    {
      title: "Defining Moment Story",
      icon: SparklesIcon,
      color: "text-primary",
    },
    {
      title: "Future Vision Story",
      icon: EyeIcon,
      color: "text-secondary",
    },
    {
      title: "Market Place Story",
      icon: Warehouse,
      color: "text-primary",
    },
    {
      title: "Client Success Story",
      icon: Handshake,
      color: "text-secondary",
    },
    {
      title: "Brand Story",
      icon: BarChart,
      color: "text-primary",
    },
  ]

  const chats = [
    { title: "My Personal Brand Story", timestamp: "2 hours ago" },
    { title: "Tech Startup Vision", timestamp: "Yesterday" },
    { title: "E-commerce Success Story", timestamp: "Apr 5" },
    { title: "Community Project Vision", timestamp: "Mar 28" },
  ]

  const filteredChats = chats.filter((chat) => chat.title.toLowerCase().includes(searchQuery.toLowerCase()))

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, x: "-100%" },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      x: "-100%",
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 300,
      },
    },
  }

  return (
    <motion.div
      className="h-screen w-4/5 bg-[#120E13]/95 backdrop-blur-md p-4 flex flex-col gap-4 border-r border-white/5 overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <motion.div className="flex justify-between items-center" variants={itemVariants}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
            <span className="text-white font-bold text-sm">BS</span>
          </div>
          <h2 className="text-white text-lg font-semibold">Brand Story</h2>
        </div>
        <motion.button
          onClick={onClose}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70"
          whileHover={{ rotate: 90, scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X size={18} />
        </motion.button>
      </motion.div>

      <motion.div className="w-full" variants={itemVariants}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="size-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full p-2.5 pl-10 text-sm rounded-lg text-white bg-[#1e1d1d]/70 border border-[#333]/50 focus:border-primary focus:ring-1 focus:ring-primary placeholder-gray-400"
            placeholder="Search chats..."
          />
        </div>
      </motion.div>

      <motion.div
        className="w-full bg-[#1A2C58]/60 rounded-2xl flex flex-col justify-center items-center p-4 gap-2 border border-white/5"
        variants={itemVariants}
      >
        <motion.div
          className="w-full flex justify-center items-center"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring" }}
        >
          <img
            src={Profile}
            alt="User profile"
            className="w-[60px] h-[60px] rounded-full object-cover border-2 border-secondary shadow-lg"
          />
        </motion.div>
        <motion.div className="w-full" variants={itemVariants}>
          <h3 className="text-white text-center font-medium">Rohan Balami</h3>
        </motion.div>
        <motion.div className="w-full" variants={itemVariants}>
          <h3 className="text-white/70 text-center text-xs">John.doe@gmail.com</h3>
        </motion.div>
        <motion.div className="w-full" variants={itemVariants}>
          <h3 className="text-white/80 text-center text-xs p-2 bg-white/5 rounded-lg border border-white/5">
            I'm John Doe. I want to start the business. I don't know how can I start it?
          </h3>
        </motion.div>
      </motion.div>

      <motion.div className="w-full" variants={itemVariants}>
        <h2 className="text-lg text-white font-medium flex items-center gap-2">
          <span className="size-2 rounded-full bg-primary"></span>
          Story Types
        </h2>
      </motion.div>

      <motion.div
        className="w-full flex flex-col gap-2 overflow-y-auto max-h-[25vh] scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent pr-1"
        variants={itemVariants}
      >
        {stories.map((story, index) => (
          <motion.div
            key={index}
            className="p-3 bg-white/90 rounded-xl flex flex-row gap-2 items-center justify-between hover:bg-white/100 transition-colors cursor-pointer shadow-sm hover:shadow-md"
            whileHover={{ x: 5, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (onSelectStory) onSelectStory(story.title)
              onClose()
            }}
          >
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-gray-100 rounded-lg">
                <story.icon className={`size-4 ${story.color}`} />
              </div>
              <span className="text-sm font-medium text-gray-800">{story.title}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div className="w-full" variants={itemVariants}>
        <h2 className="text-lg text-white font-medium flex items-center gap-2">
          <span className="size-2 rounded-full bg-secondary"></span>
          Recent Chats
        </h2>
      </motion.div>

      <motion.div
        className="w-full flex flex-col gap-2 overflow-y-auto max-h-[25vh] scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent pr-1"
        variants={itemVariants}
      >
        {filteredChats.length > 0 ? (
          filteredChats.map((chat, index) => (
            <motion.div
              key={index}
              className="p-3 bg-white/90 rounded-xl flex items-center justify-between hover:bg-white/100 transition-colors cursor-pointer shadow-sm hover:shadow-md"
              whileHover={{ x: 5, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-800 truncate">{chat.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{chat.timestamp}</span>
                <motion.button
                  className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Trash2 size={14} />
                </motion.button>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="p-4 text-center text-white/50 bg-white/5 rounded-xl">
            No chats found matching "{searchQuery}"
          </div>
        )}
      </motion.div>

      <motion.div
        className="rounded-2xl bg-[#16171BBF]/75 w-full flex flex-col gap-3 p-4 mt-auto border border-white/5"
        variants={itemVariants}
      >
        <div className="flex items-center gap-2 text-white">
          <NotebookText className="size-5 text-primary" />
          <p className="font-medium">Profile</p>
        </div>

        <motion.div
          className="p-3 bg-white rounded-xl flex flex-row gap-2 items-center justify-center"
          whileHover={{ y: -3 }}
          transition={{ type: "spring" }}
          layout
        >
          <File className="size-5 text-secondary" />
          <span className="text-sm font-medium">View Profile</span>
        </motion.div>
      </motion.div>

      <motion.div className="w-full flex flex-col gap-2 mt-2" variants={itemVariants}>
        <motion.button
          className="w-full py-2.5 text-sm font-medium btn-primary rounded-xl text-white flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Settings size={16} />
          <span>Settings</span>
        </motion.button>

        <motion.button
          className="w-full py-2.5 text-sm font-medium btn-primary rounded-xl text-white flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <LifeBuoy size={16} />
          <span>Support</span>
        </motion.button>

        <motion.button
          className="w-full py-2.5 text-sm font-medium btn-primary rounded-xl text-white flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <LogOut size={16} />
          <span>Logout</span>
        </motion.button>
      </motion.div>
    </motion.div>
  )
}

export default MobileMenu
